/* =============================================================================
 * OiOi - Gift With Purchase (GWP) controller  -  assets/app.js  -  v6
 *
 * v6 = the proven v5 controller (conservative single-flight serialization, the
 * money/decline/dedupe/give-up logic) with ONE added capability: DYNAMIC GIFT
 * COLOUR. The gift is no longer hardcoded to chestnut; it is whichever wallet
 * colour is in stock, in priority order (chestnut -> black -> oat), so the full
 * pooled stock is used instead of bottlenecking on one colour. Two behaviours:
 *   - ADD picks the active in-stock colour (window.GWP.giftColours).
 *   - SWAP: if a gift colour sells out, the controller swaps that line for an
 *     in-stock colour the moment the customer engages their cart (or removes the
 *     gift if every colour is gone - "while stocks last").
 * Liveness signal: the AUTHORITATIVE source is the /cart/add.js response (a 422
 * means genuinely sold out -> that colour is marked sold-out for the session and
 * we move on); a /products/{handle}.js availability check is a secondary/initial
 * signal. We FAIL OPEN on an unknown reading so a transient fetch error never
 * destroys a working gift. Everything else below is unchanged from v5.
 *
 * ---------------------------------------------------------------------------
 * v5/v4 PROVENANCE (unchanged):
 * WHAT THE THEME DOES TO US (Empire base / Pixel Union AsyncView):
 *   window.GWP.evaluate(cart)             // SYNCHRONOUSLY at the TOP of
 *                                         // StaticCartDrawer.loadContent (mid-render).
 *   window.GWP.reconcile(cart, onChanged) // inside StaticCart._didUpdate AFTER
 *                                         // the DOM swap settles (called TWICE; the
 *                                         // 2nd onChanged throws).
 *   window.GWP.markDeclined()             // when the user removes a [data-gwp-gift] line.
 *   loadContent() does NOT return its promise (unobservable async render).
 *
 * CONSERVATIVE SERIALIZATION: evaluate/reconcile/markDeclined ONLY schedule.
 * runPass is strict single-flight. ALL cart POSTs happen inside applyDecision
 * under the lock; after a mutating pass the lock is held SETTLE_MS before release;
 * a non-mutating pass releases immediately; rerun coalesces. Mutations can never
 * run while a render is in flight, so the cart cannot freeze.
 *
 * MONEY/DECLINE/ELIGIBILITY/CURRENCY: line-level BXGY zeroes the gift only on the
 * next /cart.js recalc (never judge free-ness from add.js); per-line-key paid
 * observation; persisted _gwp_declined and _gwp_giveup; currency gate; bounded
 * back-offs + terminal give-up. (Full notes in the v5 history; logic preserved.)
 * ========================================================================== */
(function () {
  "use strict";

  /* ----------------------------- constants -------------------------------- */
  var GIFT_PROP = "_gwp";              // line-item property marking our gift
  var DECLINED_ATTR = "_gwp_declined"; // cart attribute persisting user decline
  var GIVEUP_ATTR = "_gwp_giveup";     // cart attribute persisting broken-discount give-up

  var THRESHOLD_CURRENCY_DEFAULT = "AUD";

  // v6.4: RESETTING debounce window for any scheduled reconcile. Raised 90 -> 400 and
  // made resetting (see scheduleReconcile) so a burst of cart edits (e.g. deleting two
  // items quickly, which the theme re-renders one-by-one) COALESCES into a single pass
  // that runs only AFTER the cart goes quiet. This stops our gift add/strip + drawer
  // refresh from landing on top of the theme's in-flight delete re-renders, which was
  // corrupting the drawer (garbled "undefined" line items on rapid deletes below $220).
  var SCHEDULE_DEBOUNCE_MS = 400;

  // v6.5: cart "hot-guard". After ANY cart event (the theme calls our hooks on every
  // add / remove / quantity change), wait until the cart has been quiet for this long
  // before we read or mutate. This keeps the controller from issuing a /cart/change.js
  // while the theme's own delete is still settling - that collision was the FIRST 400,
  // which then fanned out into a /cart.json refetch storm from the installed cart apps
  // and tripped Shopify's 429 throttle. We verified against the live empire.js that the
  // theme removes lines by stable KEY (not index), so our mid-burst mutation was the
  // only in-our-control trigger; deferring until quiet removes it AND cuts our request
  // volume during a burst (we bail before fetching), so we stop feeding the storm.
  var HOT_GUARD_MS = 700;

  // CONSERVATIVE SETTLE WINDOW. After a MUTATING pass, hold the single-flight
  // lock this long before release so we can NEVER begin another pass while the
  // theme's unobservable post-mutation render is in flight.
  var SETTLE_MS = 1500;

  // After an ADD, time Shopify needs to run its automatic-discount recalc before
  // we may judge a non-zero gift line "paid".
  var DISCOUNT_RECALC_GRACE_MS = 8000;

  // Back-off after a failed add, so a sold-out variant or a missing discount
  // cannot produce an add-on-every-render storm.
  var ADD_BACKOFF_MS = 60000;

  // Consecutive recalc passes we tolerate a non-zero (and not allocation-covered)
  // gift, PER LINE KEY, before stripping it as paid.
  var PAID_OBSERVE_LIMIT = 3;

  // Back-off after a failed/incomplete strip, so the self-heal cannot livelock on
  // a stale key, and the bounded count before we latch giveUp.
  var STRIP_BACKOFF_MS = 5000;
  var STRIP_FAILURE_LIMIT = 3;

  // v6: how long to trust a resolved colour-availability snapshot before
  // re-fetching /products/{handle}.js. The authoritative add-422 signal updates
  // `soldOut` immediately regardless of this cache. v6.1: tightened 15s -> 8s so a
  // held gift whose colour sells out is detected (and swapped) sooner.
  var COLOUR_CACHE_MS = 8000;

  /* ------------------------------- state ---------------------------------- */
  var inFlight = false;
  var rerun = false;
  var pendingOnChanged = null;
  var declinedFlag = false;
  var pendingDeclineClear = false;
  var declineDirty = false;
  var addBackoffUntil = 0;
  var stripBackoffUntil = 0;
  var stripFailures = 0;
  var lastAddAt = 0;
  var paidObs = {};
  var giveUp = false;
  var giveUpDirty = false;
  var scheduleTimer = null;
  // v6.5: timestamp of the most recent cart event (any theme hook firing). Drives the hot-guard.
  var lastCartEventAt = 0;
  var verifyTimer = null;
  // v6.1: single-shot timer that re-passes promptly after a 422 sell-out so we
  // advance to the next in-stock colour without waiting on a user trigger.
  var colourRetryTimer = null;

  // v6.2 (idle-edge): finalize() support. A best-effort, AWAITABLE "settle the gift
  // now" used by the checkout-button guard, and fire-and-forget on tab focus/visibility.
  // passCounter numbers each pass that actually STARTS; a finalize() waiter records
  // the number of the first pass that will run after it was requested (passCounter+1)
  // and resolves once a pass with that number (or later) has completed. This adds NO
  // background polling - it only ever causes a pass when something asks for one.
  var passCounter = 0;
  var finalizeWaiters = [];

  // v6: cached colour-availability snapshot { at, state }.
  var coloursCache = { at: 0, state: null };
  // v6: colours confirmed sold out THIS SESSION via an authoritative /cart/add.js
  // 422. This overrides any (CDN-cacheable) product-availability reading, so the
  // controller reacts to a real sell-out immediately. { vid: true }.
  var soldOut = {};

  /* --------------------------- tiny utilities ----------------------------- */
  function cfg() {
    var g = window.GWP || {};
    return {
      on: !!g.eligible,
      variantId: toInt(g.freeProductVarientId),
      minCents: Math.round((Number(g.minCartPrice) || 0) * 100),
      currency: String(g.minCartCurrency || g.currency || THRESHOLD_CURRENCY_DEFAULT).toUpperCase(),
      tag: g.eligibleProductTag || null,
      triggerHandles: normaliseHandles(g.triggerHandles),
      // v6: ordered candidate gift colours [{vid, handle}]. Empty -> single-colour
      // mode (no live check, no swap; preserves pre-v6 behaviour).
      colours: normaliseColours(g.giftColours, toInt(g.freeProductVarientId)),
      // v2 (colour-match): handle -> matching scrunchie vid, and the fallback vid.
      giftMap: normaliseGiftMap(g.giftMap),
      giftMapDefault: toInt(g.giftMapDefault)
    };
  }

  function toInt(v) {
    var n = parseInt(v, 10);
    return isNaN(n) ? 0 : n;
  }

  // v6: build a clean ordered colour list from window.GWP.giftColours. Each entry
  // needs a numeric variant id AND a product handle (used for the availability
  // fetch). Anything malformed is dropped.
  function normaliseColours(list, fallbackVid) {
    var out = [];
    if (list && list.length) {
      for (var i = 0; i < list.length; i++) {
        var item = list[i] || {};
        var vid = toInt(item.vid || item.id || item.variantId);
        var handle = item.handle;
        if (vid && handle) out.push({ vid: vid, handle: String(handle) });
      }
    }
    return out;
  }

  // Retarget: clean lower-cased list of product handles that make a cart eligible.
  function normaliseHandles(list) {
    var out = [];
    if (list && list.length) {
      for (var i = 0; i < list.length; i++) {
        var h = list[i];
        if (h) out.push(String(h).toLowerCase());
      }
    }
    return out;
  }

  // Retarget: true when the cart holds at least one NON-gift line whose product handle is
  // in the configured trigger list. Gift/wallet lines are excluded so the gift can never
  // make the cart self-eligible (same exclusion the old subtotal path applied).
  function cartHasTrigger(cart, c) {
    var handles = (c && c.triggerHandles) || [];
    if (!handles.length) return false;
    var vids = walletVids(c);
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      var line = items[i];
      if (isWalletLine(line, vids)) continue;
      var h = String(line.handle || "").toLowerCase();
      for (var j = 0; j < handles.length; j++) {
        if (h === handles[j]) return true;
      }
    }
    return false;
  }

  // v2 (colour-match): build a plain map of LOWER-CASED trigger handle -> integer
  // scrunchie variant id. Malformed entries (missing handle or non-integer vid)
  // are dropped so a bad Liquid lookup can never poison the resolver.
  function normaliseGiftMap(obj) {
    var out = {};
    if (obj && typeof obj === "object") {
      for (var k in obj) {
        if (!Object.prototype.hasOwnProperty.call(obj, k)) continue;
        var vid = toInt(obj[k]);
        if (k && vid) out[String(k).toLowerCase()] = vid;
      }
    }
    return out;
  }

  // v2 (colour-match): the scrunchie colour we WANT, matched to the corduroy
  // product in the cart. Walk the cart lines IN ORDER and, for the FIRST non-gift
  // line whose handle is mapped, return its mapped vid - "first corduroy in cart
  // wins" (recomputed each pass, so it re-picks if that line is later removed).
  // Gift/scrunchie lines are skipped via isWalletLine so the gift can never match
  // itself. No mapped line -> giftMapDefault (blue), else the last configured
  // colour vid, else the single configured variant.
  function desiredGiftVid(cart, c) {
    var map = (c && c.giftMap) || {};
    var vids = walletVids(c);
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      var line = items[i];
      if (isWalletLine(line, vids)) continue;
      var h = String(line.handle || "").toLowerCase();
      if (Object.prototype.hasOwnProperty.call(map, h)) return map[h];
    }
    if (c && c.giftMapDefault) return c.giftMapDefault;
    if (c && c.colours && c.colours.length) return c.colours[c.colours.length - 1].vid;
    return c ? c.variantId : 0;
  }

  function cartJsUrl() {
    var r = (window.Theme && window.Theme.routes) || (window.Shopify && window.Shopify.routes) || {};
    return (r.cart_url ? r.cart_url + ".js" : "/cart.js");
  }

  function routes() {
    var r = (window.Theme && window.Theme.routes) || (window.Shopify && window.Shopify.routes) || {};
    return {
      add:    r.cart_add_url    || "/cart/add.js",
      change: r.cart_change_url || "/cart/change.js",
      update: r.cart_url ? (r.cart_url + "/update.js") : "/cart/update.js"
    };
  }

  function getJSON(url) {
    return fetch(url, {
      method: "GET",
      headers: { "Accept": "application/json" },
      credentials: "same-origin",
      cache: "no-store"
    }).then(readJSON);
  }

  function postJSON(url, body) {
    return fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", "Accept": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify(body)
    }).then(function (res) {
      return readJSON(res).then(function (data) {
        return { ok: res.ok, status: res.status, data: data };
      });
    });
  }

  function readJSON(res) {
    return res.text().then(function (t) {
      try { return t ? JSON.parse(t) : {}; }
      catch (e) { return {}; }
    });
  }

  /* ----------------------- v6: colour availability ------------------------ */
  // Resolve { active: vid|null, available: {vid: true|false|undefined}, dynamic }.
  //   available[vid] === true     -> in stock
  //   available[vid] === false    -> KNOWN sold out (acted on: add skips it, swap fires)
  //   available[vid] === undefined-> unknown (transient error / not checked): NEVER
  //                                  treated as sold out, so a working gift is never
  //                                  destroyed on a flaky read.
  // active = first colour KNOWN in stock; else first UNKNOWN colour (so adds still
  // attempt and self-correct via the add-422 -> soldOut signal); else null (every
  // colour KNOWN sold out -> while-stocks-last, gift removed).
  function resolveColours(c, desiredVid) {
    var colours = c.colours;
    if (!colours || !colours.length) {
      // Single-colour mode (no list configured): the configured variant is the
      // active gift and we never swap. Preserves pre-v6 behaviour exactly.
      return Promise.resolve({ active: c.variantId || null, available: {}, dynamic: false });
    }

    var now = Date.now();
    if (coloursCache.state && (now - coloursCache.at) < COLOUR_CACHE_MS) {
      return Promise.resolve(applySoldOut(colours, coloursCache.state, desiredVid));
    }

    return Promise.all(colours.map(function (col) {
      return getJSON("/products/" + col.handle + ".js").then(function (p) {
        var avail; // undefined = unknown
        var variants = (p && p.variants) || null;
        if (variants) {
          avail = false;
          for (var i = 0; i < variants.length; i++) {
            if (Number(variants[i].id) === col.vid) { avail = !!variants[i].available; break; }
          }
        }
        return { vid: col.vid, available: avail };
      }).catch(function () {
        return { vid: col.vid, available: undefined }; // unknown -> never acted on as sold out
      });
    })).then(function (results) {
      var available = {};
      for (var i = 0; i < colours.length; i++) {
        var vid = colours[i].vid;
        var r = null;
        for (var j = 0; j < results.length; j++) { if (results[j].vid === vid) { r = results[j]; break; } }
        available[vid] = r ? r.available : undefined;
      }
      var base = { available: available, dynamic: true };
      var state = applySoldOut(colours, base, desiredVid);
      coloursCache = { at: now, state: { available: available, dynamic: true } };
      return state;
    });
  }

  // Overlay the authoritative session sold-out set and (re)compute the active
  // colour. Kept separate so a cached snapshot still honours sold-outs learned
  // since it was taken.
  function applySoldOut(colours, snapshot, desiredVid) {
    var available = {};
    var i;
    for (i = 0; i < colours.length; i++) {
      var vid = colours[i].vid;
      available[vid] = soldOut[vid] ? false : snapshot.available[vid];
    }
    var active = null;
    // v2 (colour-match): PRIORITY - the matched colour wins whenever it is one of
    // the offered colours and NOT known sold out (true or unknown both qualify).
    // With CONTINUE inventory it is always addable, so the match always wins; a
    // KNOWN-false colour falls through to the existing waterfall below, preserving
    // sold-out safety.
    if (desiredVid) {
      for (i = 0; i < colours.length; i++) {
        if (colours[i].vid === desiredVid) {
          if (available[desiredVid] !== false) active = desiredVid;
          break;
        }
      }
    }
    if (active === null) {
      for (i = 0; i < colours.length; i++) {
        if (available[colours[i].vid] === true) { active = colours[i].vid; break; }
      }
    }
    if (active === null) {
      // No colour KNOWN in stock. Prefer the first UNKNOWN colour (transient read)
      // so adds still attempt and self-correct; else null (all KNOWN sold out).
      for (i = 0; i < colours.length; i++) {
        if (available[colours[i].vid] === undefined) { active = colours[i].vid; break; }
      }
    }
    return { active: active, available: available, dynamic: true };
  }

  /* --------------------------- cart introspection ------------------------- */
  // v6.3 - the `_gwp` tag is the controller's AUTHORITY boundary. There are two
  // distinct notions of "a wallet" now, because the Card Wallet is ALSO a normally
  // purchasable product:
  //   * managedGiftLines (lineHasGwp)  - lines THIS controller added as the gift.
  //     ONLY these may be classified free/paid, deduped, swapped, or stripped. A
  //     customer's own wallet purchase is NEVER touched (no charge-removal, no
  //     give-up trigger).
  //   * walletLines (any gift colour OR _gwp) - used for the $220 threshold
  //     (excluded so a wallet can't be gamed toward eligibility) and for the
  //     "already has a wallet, don't add a duplicate" check. This recognises ALL
  //     three colours, not just the configured one, so a pre-added black/oat wallet
  //     no longer triggers a second gift being added.
  function lineHasGwp(line) {
    var props = (line && line.properties) || {};
    return props[GIFT_PROP] === true || props[GIFT_PROP] === "true" || props[GIFT_PROP] === "1";
  }

  // Map {vid:true} of every variant that IS the gift product in any offered colour
  // (plus the configured single variant). Derived from the live cfg().
  function walletVids(c) {
    var set = {};
    if (c && c.variantId) set[Number(c.variantId)] = true;
    var cols = (c && c.colours) || [];
    for (var i = 0; i < cols.length; i++) {
      if (cols[i] && cols[i].vid) set[Number(cols[i].vid)] = true;
    }
    return set;
  }

  function isWalletLine(line, vids) {
    if (!line) return false;
    if (lineHasGwp(line)) return true;
    return !!(vids && vids[Number(line.variant_id)]);
  }

  // Lines the controller OWNS (added with the _gwp tag). The only lines it may mutate.
  function managedGiftLines(cart) {
    var items = (cart && cart.items) || [];
    var out = [];
    for (var i = 0; i < items.length; i++) {
      if (lineHasGwp(items[i])) out.push(items[i]);
    }
    return out;
  }

  // Any wallet line at all (our gift OR a customer-added wallet of any gift colour).
  function walletLines(cart, c) {
    var vids = walletVids(c);
    var items = (cart && cart.items) || [];
    var out = [];
    for (var i = 0; i < items.length; i++) {
      if (isWalletLine(items[i], vids)) out.push(items[i]);
    }
    return out;
  }

  function lineDiscountAllocations(line) {
    if (!line) return null;
    if (line.discount_allocations && line.discount_allocations.length) {
      return line.discount_allocations;
    }
    if (line.line_level_discount_allocations && line.line_level_discount_allocations.length) {
      return line.line_level_discount_allocations;
    }
    return null;
  }

  function giftIsFree(line) {
    if (!line) return false;
    var flp = Number(line.final_line_price);
    if (!isNaN(flp) && flp === 0) return true;
    var original = Number(
      line.original_line_price != null ? line.original_line_price : line.line_price
    );
    if (isNaN(original)) return false;
    if (original === 0) return true;
    var allocs = lineDiscountAllocations(line);
    if (allocs && allocs.length) {
      var covered = 0;
      for (var i = 0; i < allocs.length; i++) {
        var amt = Number(allocs[i] && allocs[i].amount);
        if (!isNaN(amt)) covered += amt;
      }
      if (covered >= original) return true;
    }
    return false;
  }

  // Subtotal of NON-wallet lines only. Every wallet colour is excluded so a wallet
  // (gift or a customer-added one) can never count toward the $220 eligibility.
  function nonGiftSubtotalCents(cart, c) {
    var vids = walletVids(c);
    var items = (cart && cart.items) || [];
    var sum = 0;
    for (var i = 0; i < items.length; i++) {
      var line = items[i];
      if (isWalletLine(line, vids)) continue;
      var v = Number(line.final_line_price != null ? line.final_line_price : line.line_price);
      if (!isNaN(v)) sum += v;
    }
    return sum;
  }

  function nonGiftCount(cart, c) {
    var vids = walletVids(c);
    return ((cart && cart.items) || []).filter(function (l) {
      return !isWalletLine(l, vids);
    }).length;
  }

  function cartCurrency(cart) {
    return String((cart && (cart.currency || cart.presentment_currency)) || "").toUpperCase();
  }

  function cartDeclined(cart) {
    var attrs = (cart && cart.attributes) || {};
    var v = attrs[DECLINED_ATTR];
    return v === true || v === "true" || v === "1";
  }

  function cartGaveUp(cart) {
    var attrs = (cart && cart.attributes) || {};
    var v = attrs[GIVEUP_ATTR];
    return v === true || v === "true" || v === "1";
  }

  /* --------------------------- cart mutations ----------------------------- */
  function setDeclinedAttr(value) {
    var body = { attributes: {} };
    body.attributes[DECLINED_ATTR] = value ? "true" : "";
    return postJSON(routes().update, body);
  }

  function setGiveUpAttr(value) {
    var body = { attributes: {} };
    body.attributes[GIVEUP_ATTR] = value ? "true" : "";
    return postJSON(routes().update, body);
  }

  function addGift(variantId) {
    var props = {};
    props[GIFT_PROP] = "true";
    return postJSON(routes().add, { id: variantId, quantity: 1, properties: props });
  }

  // v6: record an authoritative sell-out. A /cart/add.js 422 means the variant is
  // genuinely unavailable -> mark it so resolveColours skips it immediately and any
  // existing line of that colour is swapped, without waiting on the CDN-cacheable
  // product availability to refresh. Also invalidate the snapshot so `active`
  // recomputes on the next pass.
  function noteSoldOut(variantId) {
    if (variantId) {
      soldOut[variantId] = true;
      coloursCache = { at: 0, state: null };
    }
  }

  function removeLineByKey(key) {
    if (!key) return Promise.resolve(null);
    return postJSON(routes().change, { id: key, quantity: 0 }).then(function (r) {
      return r.data;
    });
  }

  function setLineQty(key, qty) {
    if (!key) return Promise.resolve(null);
    return postJSON(routes().change, { id: key, quantity: qty }).then(function (r) {
      return r.data;
    });
  }

  function removeKeys(keys) {
    if (!keys || !keys.length) return Promise.resolve(false);
    var targeted = {};
    for (var i = 0; i < keys.length; i++) targeted[keys[i]] = true;
    return keys.reduce(function (p, k) {
      return p.then(function () { return removeLineByKey(k); });
    }, Promise.resolve()).then(function () {
      return getJSON(cartJsUrl()).then(function (fresh) {
        var items = (fresh && fresh.items) || [];
        for (var j = 0; j < items.length; j++) {
          if (targeted[items[j].key]) {
            throw new Error("gwp: strip incomplete, line survived");
          }
        }
        return true;
      });
    });
  }

  // v3 (superseded the v6.3 managed-only rule): remove OUR managed lines (_gwp) AND any
  // gift-only $0 variant line. See the SAFE-ONLY note in the body: this holds only
  // because the gift variants are gift-only (never sellable). A customer's own purchase
  // of the SELLABLE scrunchie (a different variant id) is never stripped.
  function stripAllGifts(cart) {
    // v3 (0-dollar gift, direct-URL closure): strip not only OUR managed gift lines
    // (_gwp) but ALSO any line whose variant is a gift-only variant (the hidden
    // 0-dollar gift products), even without the _gwp property. Those variants are
    // NEVER a real purchase (customers buy the separate sellable scrunchies at
    // different variant ids), so removing a manually-added gift reached via the
    // Unlisted product's direct URL can never strip a genuine purchase. stripAllGifts
    // is only ever reached from mutateStrip and the not-eligible + declined path, i.e.
    // when the cart does NOT qualify, so a legitimate controller-added gift (which is
    // handled entirely on the eligible path) is never routed through here. SAFE ONLY
    // because the gift variants are gift-only; if the gift is ever also sellable,
    // revert this to managed-only (managedGiftLines).
    var items = (cart && cart.items) || [];
    var vids = walletVids(cfg());
    var keys = [];
    for (var i = 0; i < items.length; i++) {
      if (isWalletLine(items[i], vids)) keys.push(items[i].key);
    }
    if (!keys.length) return Promise.resolve(false);
    return removeKeys(keys);
  }

  // v6.3: dedupe only among OUR managed gifts - keep one free, strip the rest.
  function keepOneFreeStripRest(cart) {
    var gifts = managedGiftLines(cart);
    var keepKey = null;
    for (var i = 0; i < gifts.length; i++) {
      if (giftIsFree(gifts[i])) { keepKey = gifts[i].key; break; }
    }
    if (keepKey == null) return Promise.resolve(false);
    var toRemove = [];
    for (var j = 0; j < gifts.length; j++) {
      if (gifts[j].key !== keepKey) toRemove.push(gifts[j].key);
    }
    if (!toRemove.length) return Promise.resolve(false);
    return removeKeys(toRemove);
  }

  // v6: swap a sold-out gift colour for an in-stock one UNDER THE LOCK: remove the
  // old line, then add the active colour. Same discipline as the add path - a 422
  // marks the new colour sold out and backs off; the next pass re-resolves and
  // either tries the next colour or removes the gift (while-stocks-last). We remove
  // before adding so there is never a transient second (chargeable) gift line.
  function mutateSwap(oldKey, newVid) {
    return removeLineByKey(oldKey).then(function () {
      return addGift(newVid).then(function (resp) {
        if (resp && resp.ok) {
          lastAddAt = Date.now();
          paidObs = {};
          scheduleVerifyAfterRecalc();
        } else if (resp && resp.status === 422) {
          // v6.1: the swap target also just sold out. Mark it and advance to the
          // next colour PROMPTLY - do NOT arm the 60s storm back-off, which would
          // leave the cart gift-less for a minute while another colour is in stock.
          noteSoldOut(newVid);
          scheduleColourRetry();
        } else {
          addBackoffUntil = Date.now() + ADD_BACKOFF_MS; // transient error: throttle
        }
        return { mutated: true };
      });
    }).catch(function () {
      return { mutated: true };
    });
  }

  /* ------------------------ per-line paid bookkeeping --------------------- */
  function prunePaidObs(presentKeys) {
    var keep = {};
    for (var i = 0; i < presentKeys.length; i++) {
      var k = presentKeys[i];
      if (paidObs[k] != null) keep[k] = paidObs[k];
    }
    paidObs = keep;
  }

  function bumpPaidObs(key) {
    paidObs[key] = (paidObs[key] || 0) + 1;
    return paidObs[key];
  }

  function clearPaidObs(key) {
    if (key == null) { paidObs = {}; return; }
    delete paidObs[key];
  }

  /* --------------------------- the decision core -------------------------- */
  // v6: applyDecision resolves the live colour state FIRST (cached), then threads
  // it through the unchanged v5 decision so the add uses the active in-stock colour
  // and a sold-out gift colour is swapped. Everything else is byte-for-byte v5.
  function applyDecision(cart) {
    var c = cfg();
    if (cartGaveUp(cart)) giveUp = true;

    // v2 (colour-match): pick the scrunchie colour matched to the cart's corduroy
    // product BEFORE resolving live stock, then thread it through.
    var desiredVid = desiredGiftVid(cart, c);
    return resolveColours(c, desiredVid).then(function (colourState) {
      // v5 FIX 1: flush a pending decline-attr persist HERE, under the lock, before
      // any decision branch. markDeclined/clearDeclined no longer POST; they set
      // declinedFlag + declineDirty and schedule. Persist under the lock, count as
      // mutated so SETTLE_MS applies, then chain the rest of the decision.
      if (declineDirty) {
        var wantDeclined = declinedFlag;
        declineDirty = false;
        return setDeclinedAttr(wantDeclined ? "true" : "").then(function () {
          return decideEligible(cart, c, colourState);
        }).catch(function () {
          return decideEligible(cart, c, colourState);
        }).then(function (res) {
          return { mutated: true };
        });
      }
      return decideEligible(cart, c, colourState);
    });
  }

  function decideEligible(cart, c, colourState) {
    // v5 FIX 1: flush a pending give-up persist HERE under the lock.
    if (giveUpDirty) {
      giveUpDirty = false;
      return setGiveUpAttr(true).catch(function () {}).then(function () {
        return decideEligibleInner(cart, c, colourState);
      }).then(function (res) {
        return { mutated: true };
      });
    }
    return decideEligibleInner(cart, c, colourState);
  }

  function decideEligibleInner(cart, c, colourState) {
    // Master off / misconfigured: strip any stray OWNED gift only.
    if (!c.on || !c.variantId || !(c.triggerHandles && c.triggerHandles.length)) {
      return mutateStrip(cart);
    }

    // CURRENCY GATE retired (v5, 21 Jul): the corduroy gift variants are natively $0
    // in every market (USD/GBP contextual prices verified), so a non-AUD cart can never
    // be CHARGED for the gift - the risk the June wallet gate protected against (its
    // AUD-scoped discount did not apply abroad). The paid-gift observation net below
    // still strips any gift that is somehow not free, in any currency.

    // v6.3: `managed` = OUR gifts (the only lines we may classify/dedupe/swap/strip).
    // `anyWallet` = any wallet colour present (ours OR a customer's own purchase),
    // used only to decide eligibility-exclusion and to avoid adding a duplicate.
    var managed = managedGiftLines(cart);
    var anyWallet = walletLines(cart, c);
    prunePaidObs(managed.map(function (l) { return l.key; }));

    var realCount = nonGiftCount(cart, c);
    var eligible = realCount > 0 && cartHasTrigger(cart, c);

    var userDeclined = cartDeclined(cart) || declinedFlag;

    if (!eligible) {
      addBackoffUntil = 0;
      paidObs = {};
      var declaredDeclined = cartDeclined(cart) || declinedFlag;
      if (declaredDeclined) {
        declinedFlag = false;
        pendingDeclineClear = true;
        return setDeclinedAttr(false).then(function () {
          return stripAllGifts(cart).then(function () { return { mutated: true }; });
        }).catch(function () {
          return { mutated: true };
        });
      }
      if (pendingDeclineClear && cartDeclined(cart)) {
        return setDeclinedAttr(false).then(function () {
          return { mutated: true };
        });
      }
      pendingDeclineClear = false;
      return mutateStrip(cart);
    }

    pendingDeclineClear = false;

    if (giveUp) {
      suppressInvite();
      if (!cartGaveUp(cart)) giveUpDirty = true;
      return mutateStrip(cart);
    }

    /* ------ OUR gift lines present: classify free vs paid, dedupe, normalise --- */
    if (managed.length) {
      for (var qi = 0; qi < managed.length; qi++) {
        if (Number(managed[qi].quantity) > 1) {
          var fixKey = managed[qi].key;
          return setLineQty(fixKey, 1).then(function () {
            return { mutated: true };
          }).catch(function () {
            return { mutated: true };
          });
        }
      }

      var freeGifts = [];
      var paidGifts = [];
      for (var gi = 0; gi < managed.length; gi++) {
        if (giftIsFree(managed[gi])) freeGifts.push(managed[gi]);
        else paidGifts.push(managed[gi]);
      }

      if (paidGifts.length) {
        var withinGrace = (Date.now() - lastAddAt) < DISCOUNT_RECALC_GRACE_MS;

        if (freeGifts.length && !userDeclined) {
          if (Date.now() < stripBackoffUntil) {
            return Promise.resolve({ mutated: false });
          }
          for (var pk = 0; pk < paidGifts.length; pk++) clearPaidObs(paidGifts[pk].key);
          return mutateKeepOneFree(cart);
        }

        if (userDeclined) {
          return mutateStrip(cart);
        }
        if (withinGrace) {
          return Promise.resolve({ mutated: false });
        }

        var anyAtLimit = false;
        for (var po = 0; po < paidGifts.length; po++) {
          if (bumpPaidObs(paidGifts[po].key) >= PAID_OBSERVE_LIMIT) anyAtLimit = true;
        }
        if (!anyAtLimit) {
          scheduleVerifyAfterRecalc();
          return Promise.resolve({ mutated: false });
        }

        if (Date.now() < stripBackoffUntil) {
          return Promise.resolve({ mutated: false });
        }

        paidObs = {};
        return stripAllGifts(cart).then(function (changed) {
          tripGiveUp();
          return { mutated: changed !== false };
        }).catch(function () {
          stripBackoffUntil = Date.now() + STRIP_BACKOFF_MS;
          stripFailures++;
          if (stripFailures >= STRIP_FAILURE_LIMIT) {
            tripGiveUp();
          }
          return { mutated: true };
        });
      }

      // All OUR gift lines are FREE.
      for (var fk = 0; fk < freeGifts.length; fk++) clearPaidObs(freeGifts[fk].key);

      if (userDeclined) {
        return mutateStrip(cart);
      }
      if (managed.length === 1) {
        // v6 DYNAMIC COLOUR SWAP. Our single gift is free and otherwise correct.
        // If its colour is KNOWN sold out, swap it for an in-stock colour so it is
        // not dropped at checkout (DENY policy); if every colour is gone, remove it
        // (while-stocks-last). An UNKNOWN reading keeps the gift untouched.
        if (colourState && colourState.dynamic) {
          var giftVid = Number(managed[0].variant_id);
          var active = colourState.active;
          var heldSoldOut = colourState.available[giftVid] === false;
          // v2 (colour-match): swap the held gift when it is either KNOWN sold out
          // (original v6 behaviour) OR no longer the colour the cart maps to (the
          // corduroy that set the colour was removed or changed). Only swap to a
          // colour that is not known sold out; the swap target is colourState.active,
          // which is now the matched colour. active === giftVid means already correct
          // and in stock, so no swap and no oscillation.
          var activeOk = active && active !== giftVid && colourState.available[active] !== false;
          if (activeOk) {
            return mutateSwap(managed[0].key, active);
          }
          if (heldSoldOut && !active) {
            return mutateStrip(cart); // all colours gone
          }
        }
        return Promise.resolve({ mutated: false });
      }
      if (Date.now() < stripBackoffUntil) {
        return Promise.resolve({ mutated: false });
      }
      return mutateKeepOneFree(cart);
    }

    // v6.3: no OWNED gift, but a CUSTOMER-added wallet (any colour) is already in the
    // cart. Do NOT add a duplicate - the BXGY zeroes their wallet when eligible, and
    // we never touch a line we didn't add.
    if (anyWallet.length) {
      addBackoffUntil = 0;
      return Promise.resolve({ mutated: false });
    }

    /* ------------------------ no gift line present ------------------------- */
    if (userDeclined) {
      return Promise.resolve({ mutated: false });
    }
    if (Date.now() < addBackoffUntil) {
      return Promise.resolve({ mutated: false });
    }

    return getJSON(cartJsUrl()).then(function (freshCart) {
      if (cartGaveUp(freshCart)) { giveUp = true; }
      if (declinedFlag || cartDeclined(freshCart)) {
        return stripAllGifts(freshCart).then(function (ch) {
          return { mutated: ch !== false };
        }).catch(function () { return { mutated: true }; });
      }
      // Suppress the add if ANY wallet (ours or a customer-added one of any colour)
      // is already present in the freshest cart - never create a duplicate.
      var freshWallets = walletLines(freshCart, c);
      if (freshWallets.length) {
        return { mutated: false };
      }
      if (!cartHasTrigger(freshCart, c)) {
        return { mutated: false };
      }
      if (giveUp) {
        suppressInvite();
        return { mutated: false };
      }
      // v6: add the ACTIVE in-stock colour (chestnut -> black -> oat). If no colour
      // is in stock, add nothing (while-stocks-last).
      var addVid = (colourState && colourState.dynamic) ? colourState.active : c.variantId;
      if (!addVid) {
        return { mutated: false };
      }
      return addGift(addVid).then(function (resp) {
        if (!resp.ok) {
          if (resp.status === 422) {
            // v6.1: genuinely sold out -> mark it and advance to the next colour
            // PROMPTLY (no 60s back-off; another colour may still be in stock).
            noteSoldOut(addVid);
            scheduleColourRetry();
          } else {
            addBackoffUntil = Date.now() + ADD_BACKOFF_MS; // transient error: throttle
          }
          return { mutated: false };
        }
        lastAddAt = Date.now();
        paidObs = {};
        scheduleVerifyAfterRecalc();
        return { mutated: true };
      });
    });
  }

  function mutateStrip(cart) {
    return stripAllGifts(cart).then(function (changed) {
      if (changed !== false) stripFailures = 0;
      return { mutated: changed !== false };
    }).catch(function () {
      stripBackoffUntil = Date.now() + STRIP_BACKOFF_MS;
      stripFailures++;
      if (stripFailures >= STRIP_FAILURE_LIMIT) tripGiveUp();
      return { mutated: true };
    });
  }

  function mutateKeepOneFree(cart) {
    return keepOneFreeStripRest(cart).then(function (changed) {
      if (changed !== false) stripFailures = 0;
      return { mutated: changed !== false };
    }).catch(function () {
      stripBackoffUntil = Date.now() + STRIP_BACKOFF_MS;
      stripFailures++;
      if (stripFailures >= STRIP_FAILURE_LIMIT) tripGiveUp();
      return { mutated: true };
    });
  }

  function tripGiveUp() {
    giveUp = true;
    addBackoffUntil = 0;
    giveUpDirty = true;
    suppressInvite();
  }

  function suppressInvite() {
    try {
      var nodes = document.querySelectorAll("[data-gwp-invite], .gwp-invite, [data-gwp-promo]");
      for (var i = 0; i < nodes.length; i++) {
        nodes[i].style.display = "none";
      }
    } catch (e) { /* cosmetic only */ }
  }

  function scheduleVerifyAfterRecalc() {
    if (verifyTimer) clearTimeout(verifyTimer);
    verifyTimer = setTimeout(function () {
      verifyTimer = null;
      scheduleReconcile();
    }, DISCOUNT_RECALC_GRACE_MS + 500);
  }

  // v6.1: after a 422 sell-out (add or swap), re-pass PROMPTLY so we advance to the
  // next in-stock colour without waiting for a user trigger - no 60s gift-less
  // window when another colour is available. Single-shot + deduped; harmless if the
  // cart no longer qualifies (the pass is idempotent). resolveColours sees the new
  // sold-out colour (noteSoldOut busted the cache) and picks the next one.
  function scheduleColourRetry() {
    if (colourRetryTimer) return;
    colourRetryTimer = setTimeout(function () {
      colourRetryTimer = null;
      scheduleReconcile();
    }, 1700);
  }

  /* ============================ SERIALIZATION ============================== */
  function scheduleReconcile() {
    // v6.4: RESET the timer on every call so rapid successive cart events (the theme
    // calls evaluate() at the start of each render and reconcile() after it settles)
    // push our pass back until there has been no cart activity for SCHEDULE_DEBOUNCE_MS.
    // We therefore mutate + refresh only when the cart/render has quiesced, never mid-burst.
    if (scheduleTimer) clearTimeout(scheduleTimer);
    scheduleTimer = setTimeout(function () {
      scheduleTimer = null;
      runPass();
    }, SCHEDULE_DEBOUNCE_MS);
  }

  function runPass() {
    if (inFlight) { rerun = true; return; }
    // v6.5: HOT-GUARD. If the cart was touched within HOT_GUARD_MS, defer WITHOUT reading
    // or mutating - re-arm and try again once it goes quiet. This is what stops us
    // colliding with an in-flight theme delete (the 400) and feeding the refetch storm
    // (the 429s). A deferred pass does not fetch /cart.js and does not increment passCounter.
    if (lastCartEventAt && (Date.now() - lastCartEventAt) < HOT_GUARD_MS) {
      scheduleReconcile();
      return;
    }
    inFlight = true;
    var passNo = ++passCounter; // v6.2: number this pass for finalize() waiters
    getJSON(cartJsUrl())
      .then(function (cart) { return applyDecision(cart); })
      .then(function (res) {
        var mutated = !!(res && res.mutated);
        if (mutated) requestSingleRefresh();
        flushFinalize(passNo); // v6.2: resolve any finalize() awaiting this pass
        releaseAfter(mutated);
      })
      .catch(function () {
        clearStuckLoadingState();
        rerun = true;
        flushFinalize(passNo); // v6.2: best-effort settle resolves even on error
        releaseAfter(false);
      });
  }

  // v6.2 (idle-edge): resolve finalize() waiters whose target pass has completed.
  // Inert (early return) whenever nothing is awaiting, so it never affects the
  // existing fire-and-forget reconcile path. Never throws.
  function flushFinalize(completedPassNo) {
    if (!finalizeWaiters.length) return;
    var still = [];
    for (var i = 0; i < finalizeWaiters.length; i++) {
      if (finalizeWaiters[i].target <= completedPassNo) {
        try { finalizeWaiters[i].resolve(); } catch (e) { /* ignore */ }
      } else {
        still.push(finalizeWaiters[i]);
      }
    }
    finalizeWaiters = still;
  }

  function releaseAfter(mutated) {
    setTimeout(function () {
      inFlight = false;
      if (rerun) {
        rerun = false;
        scheduleReconcile();
      }
    }, mutated ? SETTLE_MS : 0);
  }

  function requestSingleRefresh() {
    var onChanged = pendingOnChanged;
    pendingOnChanged = null;
    if (typeof onChanged === "function") {
      try {
        onChanged();
        return;
      } catch (e) {
        clearStuckLoadingState();
      }
    }
    refreshDrawerElement();
  }

  function refreshDrawerElement() {
    try {
      var el = document.querySelector("static-cart-drawer");
      if (el && typeof el.loadContent === "function") {
        el.loadContent();
      }
    } catch (e) {
      clearStuckLoadingState();
    }
  }

  /* ----------------------- public hooks: theme entry ---------------------- */
  // v6.5: record that the cart was just touched (drives the hot-guard). Called from
  // every theme entry point so a burst of edits keeps the controller deferred until quiet.
  function noteCartEvent() {
    lastCartEventAt = Date.now();
  }

  function evaluate(/* cart */) {
    noteCartEvent();
    scheduleReconcile();
  }

  // v6.2 (idle-edge): force-fresh, awaitable settle. Busts the colour-availability
  // cache so the next pass re-reads live stock, runs ONE pass, and resolves when
  // that pass (or a later one) completes. The checkout guard awaits this UNDER A
  // HARD TIMEOUT so a real sale is never blocked beyond it; the focus/visibility
  // hooks call evaluate() (cache-respecting, no extra traffic) instead. Always
  // eventually resolves once a pass runs; if none ever does, the caller's timeout
  // is the backstop. Adds no polling of its own.
  function finalize() {
    coloursCache = { at: 0, state: null };
    var target = passCounter + 1;
    return new Promise(function (resolve) {
      finalizeWaiters.push({ target: target, resolve: resolve });
      if (inFlight) { rerun = true; } // current pass predates the cache bust
      scheduleReconcile();
    });
  }

  function reconcile(cartFromTheme, onChanged) {
    noteCartEvent();
    if (typeof onChanged === "function") {
      pendingOnChanged = onChanged;
    }
    scheduleReconcile();
  }

  function markDeclined() {
    noteCartEvent();
    declinedFlag = true;
    pendingDeclineClear = false;
    declineDirty = true;
    scheduleReconcile();
  }

  function clearDeclined() {
    noteCartEvent();
    declinedFlag = false;
    pendingDeclineClear = false;
    declineDirty = true;
    scheduleReconcile();
  }

  /* --------------------- stuck-loading-state safety net ------------------- */
  function clearStuckLoadingState() {
    try {
      var rows = document.querySelectorAll(".cart-item.removing");
      for (var i = 0; i < rows.length; i++) rows[i].classList.remove("removing");
      var loaders = document.querySelectorAll("[data-cart-drawer-form-loader], .cart-drawer-form-loader");
      for (var j = 0; j < loaders.length; j++) {
        loaders[j].style.display = "none";
      }
    } catch (e) { /* never throw from the safety net */ }
  }

  /* ----------------------------- attachment ------------------------------- */
  function attach() {
    var g = window.GWP;
    if (!g || typeof g !== "object") {
      g = window.GWP = {};
    }
    if (g.__gwpBound) return;
    g.evaluate = evaluate;
    g.reconcile = reconcile;
    g.markDeclined = markDeclined;
    g.clearDeclined = clearDeclined;
    g.finalize = finalize; // v6.2 (idle-edge): checkout-guard / focus settle hook
    g.__gwpBound = true;
  }

  var lastRef = null;
  function watchGwpIdentity() {
    if (window.GWP !== lastRef) {
      lastRef = window.GWP;
      if (lastRef && !lastRef.__gwpBound) attach();
    }
  }

  /* --------------------------- external trigger --------------------------- */
  function onCartCountUpdate() {
    noteCartEvent();
    scheduleReconcile();
  }

  /* ------------------------------- bootstrap ------------------------------ */
  function init() {
    attach();
    lastRef = window.GWP;
    window.addEventListener("cartcount:update", onCartCountUpdate);
    setInterval(watchGwpIdentity, 1000);
    scheduleReconcile();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
